import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from './ui/Modal.jsx';
import MutterPraxisAutocomplete from './MutterPraxisAutocomplete.jsx';
import { pb } from '../lib/pocketbase.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  PRAXIS_ART_OPTIONS,
  PRAXIS_ART_DEFAULT,
  PRAXIS_STATUS_OPTIONS,
  PRAXIS_STATUS_DEFAULT,
  WIRTSCHAFTSMODELL_OPTIONS,
  WIRTSCHAFTSMODELL_DEFAULT,
  UNTERNEHMENSFORM_OPTIONS,
  UNTERNEHMENSFORM_DEFAULT,
  VERSORGUNGSFORM_OPTIONS,
  VERBUND_OPTIONS,
  VERBUND_KEINER_LABEL,
  SOFTWARE_OPTIONS,
  ABRECHNUNGSSTELLE_OPTIONS,
} from '../lib/praxis.js';
import { heuteAlsInputValue } from '../lib/aktivitaet.js';
import { ANRUF_TAGE, buildAnrufzeitOptions } from '../lib/anrufzeit.js';
import { FACHRICHTUNG_OPTIONS } from '../lib/fachrichtung.js';

const EMPTY = {
  name: '',
  name_zusatz: '',
  typ: 'praxis',
  praxis_art: PRAXIS_ART_DEFAULT,
  unternehmensform: UNTERNEHMENSFORM_DEFAULT,
  // Kein Default: leer heisst "nie erfasst" und ist nicht dasselbe
  // wie die aktiv waehlbare Option "unbekannt".
  versorgungsform: '',
  wirtschaftsmodell: WIRTSCHAFTSMODELL_DEFAULT,
  status: PRAXIS_STATUS_DEFAULT,
  fachgebiet: '',
  fachgebiet_neu: '',
  strasse: '',
  plz: '',
  ort: '',
  telefon: '',
  email: '',
  website: '',
  notizen: '',
  mutter_praxis: '',
  partner: '',
  umsatz_goae: '',
  umsatz_uvgoae: '',
  software: [],
  software_sonstiges: '',
  abrechnungsstelle: '',
  abrechnungsstelle_sonstiges: '',
  verbund: '',
  verbund_sonstiges: '',
  anruf_vm_von: '',
  anruf_vm_bis: '',
  anruf_nm_von: '',
  anruf_nm_bis: '',
  anruf_kein_vm: [],
  anruf_kein_nm: [],
  anruf_geschlossen: [],
};

export default function PraxisFormModal({ open, onClose, onSaved, praxis }) {
  const { user, isFuehrungskraftOrAdmin } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [partnerListe, setPartnerListe] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // Anzahl Filialen DIESER Praxis — falls > 0 darf sie keine Mutter bekommen
  // (sonst hätten wir 3 Ebenen). Wird beim Öffnen pro Edit nachgeladen.
  const [filialenCount, setFilialenCount] = useState(0);

  useEffect(() => {
    if (open) {
      setForm(
        praxis
          ? {
              ...EMPTY,
              ...praxis,
              // PB Number-Felder kommen als Number rein, Input-Value muss
              // String sein damit der leere Zustand "kein Wert" sauber bleibt.
              umsatz_goae: praxis.umsatz_goae ?? '',
              umsatz_uvgoae: praxis.umsatz_uvgoae ?? '',
              partner: praxis.partner ?? '',
              // Multi-select kommt als Array aus PB — gegen null absichern.
              software: Array.isArray(praxis.software) ? praxis.software : [],
              software_sonstiges: praxis.software_sonstiges ?? '',
              abrechnungsstelle: praxis.abrechnungsstelle ?? '',
              abrechnungsstelle_sonstiges: praxis.abrechnungsstelle_sonstiges ?? '',
              verbund: praxis.verbund ?? '',
              verbund_sonstiges: praxis.verbund_sonstiges ?? '',
              anruf_vm_von: praxis.anruf_vm_von ?? '',
              anruf_vm_bis: praxis.anruf_vm_bis ?? '',
              anruf_nm_von: praxis.anruf_nm_von ?? '',
              anruf_nm_bis: praxis.anruf_nm_bis ?? '',
              anruf_kein_vm: Array.isArray(praxis.anruf_kein_vm) ? praxis.anruf_kein_vm : [],
              anruf_kein_nm: Array.isArray(praxis.anruf_kein_nm) ? praxis.anruf_kein_nm : [],
              anruf_geschlossen: Array.isArray(praxis.anruf_geschlossen)
                ? praxis.anruf_geschlossen
                : [],
              mutter_praxis: praxis.mutter_praxis ?? '',
            }
          : EMPTY
      );
      setError(null);
    }
  }, [open, praxis]);

  // Filialen-Count für die aktuell bearbeitete Praxis nachladen. Wenn > 0,
  // darf sie nicht selbst zur Filiale werden (3-Ebenen-Verbot).
  useEffect(() => {
    if (!open || !praxis?.id) {
      setFilialenCount(0);
      return undefined;
    }
    let cancelled = false;
    pb.collection('praxen')
      .getList(1, 1, {
        filter: pb.filter('mutter_praxis = {:id}', { id: praxis.id }),
        fields: 'id',
      })
      .then((resp) => {
        if (!cancelled) setFilialenCount(resp.totalItems);
      })
      .catch(() => {
        if (!cancelled) setFilialenCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [open, praxis?.id]);

  // Partner-Liste nur für Führungskraft/Admin laden — Vertriebs-User
  // sehen das Empfehler-Feld nicht.
  useEffect(() => {
    if (!open || !isFuehrungskraftOrAdmin) return;
    let cancelled = false;
    pb.collection('partner')
      .getFullList({ sort: 'name' })
      .then((list) => {
        if (!cancelled) setPartnerListe(list);
      })
      .catch(() => {
        if (!cancelled) setPartnerListe([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isFuehrungskraftOrAdmin]);

  // Beim Bearbeiten die eigene ID aus der Mutter-Suche ausschließen — sonst
  // könnte man sich selbst als Mutter setzen.
  const excludeIds = useMemo(() => (praxis?.id ? [praxis.id] : []), [praxis?.id]);
  const istSelbstMutter = filialenCount > 0;

  function handleChange(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function handleMutterPraxisChange(id) {
    setForm((prev) => ({ ...prev, mutter_praxis: id ?? '' }));
  }

  function toggleSoftware(value) {
    setForm((prev) => {
      const current = Array.isArray(prev.software) ? prev.software : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, software: next };
    });
  }

  function toggleTag(field, value) {
    setForm((prev) => {
      const current = Array.isArray(prev[field]) ? prev[field] : [];
      const next = current.includes(value)
        ? current.filter((v) => v !== value)
        : [...current, value];
      return { ...prev, [field]: next };
    });
  }

  async function handleSubmit(event) {
    event.preventDefault();
    // Soft-Check Website: nicht type="url" weil Browser bei nackten Hostnames
    // ("www.example.de") meckert. Statt dessen: muss mind. einen Punkt haben.
    if (form.website && form.website.trim() && !form.website.includes('.')) {
      setError('Die Website sieht nicht wie eine URL aus (kein Punkt enthalten).');
      return;
    }

    // Filial-Hierarchie validieren (max. 2 Ebenen).
    if (form.mutter_praxis) {
      if (praxis?.id && form.mutter_praxis === praxis.id) {
        setError('Eine Praxis kann nicht ihre eigene Mutter-Praxis sein.');
        return;
      }
      if (istSelbstMutter) {
        setError(
          `Diese Praxis hat ${filialenCount} Filiale(n) und kann deshalb nicht selbst Filiale werden.`
        );
        return;
      }
      // Server-seitiger Check, dass die gewählte Mutter nicht selbst eine
      // Filiale ist — sonst hätten wir mehr als 2 Ebenen. Im Autocomplete
      // ist sie zwar schon ausgefiltert, aber wir prüfen sicherheitshalber.
      try {
        const mutterRecord = await pb.collection('praxen').getOne(form.mutter_praxis, {
          fields: 'id,mutter_praxis,name',
        });
        if (mutterRecord.mutter_praxis) {
          setError(
            `"${mutterRecord.name}" ist bereits Filiale einer anderen Praxis und kann deshalb nicht Mutter-Praxis sein.`
          );
          return;
        }
      } catch {
        setError('Die gewählte Mutter-Praxis konnte nicht geprüft werden.');
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const goae = form.umsatz_goae === '' ? null : Number(form.umsatz_goae);
      const uvgoae = form.umsatz_uvgoae === '' ? null : Number(form.umsatz_uvgoae);

      const softwareList = Array.isArray(form.software) ? form.software : [];
      const softwareHatSonstiges = softwareList.includes('sonstiges');
      const abrechnungIstSonstiges = form.abrechnungsstelle === 'sonstiges';
      const verbundIstSonstiges = form.verbund === 'sonstiges';

      const payload = {
        name: form.name,
        name_zusatz: form.name_zusatz?.trim() ?? '',
        typ: form.typ,
        praxis_art: form.praxis_art || PRAXIS_ART_DEFAULT,
        unternehmensform: form.unternehmensform || UNTERNEHMENSFORM_DEFAULT,
        versorgungsform: form.versorgungsform || '',
        verbund: form.verbund || '',
        // Freitext nur behalten, solange "Sonstiges" gewählt ist — sonst bliebe
        // ein alter Verbundname unsichtbar in der DB stehen.
        verbund_sonstiges: verbundIstSonstiges ? form.verbund_sonstiges.trim() : '',
        wirtschaftsmodell: form.wirtschaftsmodell || WIRTSCHAFTSMODELL_DEFAULT,
        status: form.status || PRAXIS_STATUS_DEFAULT,
        // fachgebiet wird im UI auf readonly gesetzt — alter Wert bleibt
        // historisch erhalten, neue Eingaben laufen über fachgebiet_neu.
        fachgebiet: form.fachgebiet,
        fachgebiet_neu: form.fachgebiet_neu || '',
        strasse: form.strasse,
        plz: form.plz,
        ort: form.ort,
        telefon: form.telefon,
        email: form.email,
        website: form.website,
        notizen: form.notizen,
        umsatz_goae: goae,
        umsatz_uvgoae: uvgoae,
        software: softwareList,
        // Freitext nur persistieren, wenn "Sonstiges" angehakt ist — sonst
        // leeren, damit alter Freitext nicht stumm weiterlebt.
        software_sonstiges: softwareHatSonstiges ? form.software_sonstiges.trim() : '',
        abrechnungsstelle: form.abrechnungsstelle || '',
        abrechnungsstelle_sonstiges: abrechnungIstSonstiges
          ? form.abrechnungsstelle_sonstiges.trim()
          : '',
        anruf_vm_von: form.anruf_vm_von || '',
        anruf_vm_bis: form.anruf_vm_bis || '',
        anruf_nm_von: form.anruf_nm_von || '',
        anruf_nm_bis: form.anruf_nm_bis || '',
        anruf_kein_vm: Array.isArray(form.anruf_kein_vm) ? form.anruf_kein_vm : [],
        anruf_kein_nm: Array.isArray(form.anruf_kein_nm) ? form.anruf_kein_nm : [],
        anruf_geschlossen: Array.isArray(form.anruf_geschlossen) ? form.anruf_geschlossen : [],
        mutter_praxis: form.mutter_praxis || null,
      };

      // Partner-Feld nur für Führungskraft/Admin senden — Vertriebs-User
      // sollen das Feld weder lesen noch überschreiben können.
      if (isFuehrungskraftOrAdmin) {
        payload.partner = form.partner || null;
      }

      // Audit-Felder fürs Umsatz-Tracking: bei jedem Wert-Wechsel mitschreiben.
      const goaeAlt = praxis?.umsatz_goae ?? null;
      const uvgoaeAlt = praxis?.umsatz_uvgoae ?? null;
      const umsatzGeaendert = goae !== goaeAlt || uvgoae !== uvgoaeAlt;
      if (umsatzGeaendert && (goae != null || uvgoae != null)) {
        payload.umsatz_erfasst_am = heuteAlsInputValue();
        payload.umsatz_erfasst_von = user?.id ?? null;
      }

      let saved;
      const istNeu = !praxis?.id;
      if (praxis?.id) {
        saved = await pb.collection('praxen').update(praxis.id, payload);
      } else {
        payload.angelegt_von = user?.id ?? null;
        saved = await pb.collection('praxen').create(payload);
      }
      onSaved?.(saved);
      onClose();
      // Bei Neuanlage direkt zur Detailseite springen, damit der User sofort
      // weiter arbeiten kann (z. B. Kontakt anlegen). Beim Bearbeiten bleibt
      // der User auf der aktuellen Seite.
      if (istNeu && saved?.id) {
        navigate(`/praxen/${saved.id}`);
      }
    } catch (err) {
      setError(err?.message || 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={praxis?.id ? 'Praxis bearbeiten' : 'Neue Praxis anlegen'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label">Name *</label>
            <input
              type="text"
              required
              className="input mt-1"
              value={form.name}
              onChange={handleChange('name')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Namenszusatz</label>
            <input
              type="text"
              className="input mt-1"
              value={form.name_zusatz}
              onChange={handleChange('name_zusatz')}
            />
            <p className="mt-1 text-xs text-slate-500">
              Optional, für lange Doppelnamen. Wird als „Name (Zusatz)"
              angezeigt.
            </p>
          </div>

          <div>
            <label className="label">Typ *</label>
            <select
              required
              className="input mt-1"
              value={form.typ}
              onChange={handleChange('typ')}
            >
              <option value="praxis">Arztpraxis</option>
              <option value="klinik">Klinik</option>
            </select>
          </div>

          <div>
            <label className="label">Praxis-Art *</label>
            <select
              required
              className="input mt-1"
              value={form.praxis_art}
              onChange={handleChange('praxis_art')}
            >
              {PRAXIS_ART_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Versorgungsform</label>
            <select
              className="input mt-1"
              value={form.versorgungsform}
              onChange={handleChange('versorgungsform')}
            >
              <option value="">— nicht erfasst —</option>
              {VERSORGUNGSFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Verbund</label>
            <select
              className="input mt-1"
              value={form.verbund}
              onChange={handleChange('verbund')}
            >
              <option value="">{VERBUND_KEINER_LABEL}</option>
              {VERBUND_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {form.verbund === 'sonstiges' && (
              <div className="mt-3">
                <label className="label">Name des Verbunds</label>
                <input
                  type="text"
                  className="input mt-1"
                  value={form.verbund_sonstiges}
                  onChange={handleChange('verbund_sonstiges')}
                />
              </div>
            )}
          </div>

          <div className="sm:col-span-2">
            <label className="label">Unternehmensform</label>
            <select
              className="input mt-1"
              value={form.unternehmensform}
              onChange={handleChange('unternehmensform')}
            >
              {UNTERNEHMENSFORM_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              Rechtsform der Praxis. Für die Versorgungsform (MVZ, BAG,
              Einzelpraxis, Klinik) nutze das eigene Feld darüber.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Status *</label>
            <select
              required
              className="input mt-1"
              value={form.status}
              onChange={handleChange('status')}
            >
              {PRAXIS_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Wirtschaftsmodell</label>
            <select
              className="input mt-1"
              value={form.wirtschaftsmodell}
              onChange={handleChange('wirtschaftsmodell')}
            >
              {WIRTSCHAFTSMODELL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">
              <strong>Gemeinschaft</strong>: mehrere Ärzt:innen teilen sich Räume, Personal
              und wirtschaftliches Risiko (z. B. Gemeinschafts-/Berufsausübungs­gemeinschaft).{' '}
              <strong>Einzeln</strong>: eine Ärztin/ein Arzt betreibt die Praxis allein.{' '}
              Wenn unklar, bitte auf <em>Unbekannt</em> lassen.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Fachgebiet (alt, Freitext)</label>
            <input
              type="text"
              readOnly
              className="input mt-1 cursor-not-allowed bg-slate-100 text-slate-500"
              value={form.fachgebiet}
            />
            <p className="mt-1 text-xs text-slate-500">
              Bitte strukturierte Fachrichtung auswählen.
            </p>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Fachrichtung (strukturiert)</label>
            <select
              className="input mt-1"
              value={form.fachgebiet_neu}
              onChange={handleChange('fachgebiet_neu')}
            >
              <option value="">— nicht erfasst —</option>
              {FACHRICHTUNG_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="label">Straße &amp; Nr.</label>
            <input
              type="text"
              className="input mt-1"
              value={form.strasse}
              onChange={handleChange('strasse')}
            />
          </div>

          <div>
            <label className="label">PLZ</label>
            <input
              type="text"
              className="input mt-1"
              value={form.plz}
              onChange={handleChange('plz')}
            />
          </div>

          <div>
            <label className="label">Ort</label>
            <input
              type="text"
              className="input mt-1"
              value={form.ort}
              onChange={handleChange('ort')}
            />
          </div>

          <div>
            <label className="label">Telefon</label>
            <input
              type="tel"
              className="input mt-1"
              value={form.telefon}
              onChange={handleChange('telefon')}
            />
          </div>

          <div>
            <label className="label">E-Mail</label>
            <input
              type="email"
              className="input mt-1"
              value={form.email}
              onChange={handleChange('email')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Website</label>
            <input
              type="text"
              className="input mt-1"
              placeholder="z. B. www.praxis-musterstadt.de"
              value={form.website}
              onChange={handleChange('website')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Notizen</label>
            <textarea
              rows={4}
              className="input mt-1"
              value={form.notizen}
              onChange={handleChange('notizen')}
            />
          </div>

          <div className="sm:col-span-2">
            <label className="label">Mutter-Praxis (Filial-Hierarchie)</label>
            <MutterPraxisAutocomplete
              value={form.mutter_praxis}
              onChange={handleMutterPraxisChange}
              excludeIds={excludeIds}
              disabled={istSelbstMutter}
            />
            {istSelbstMutter ? (
              <p className="mt-1 text-xs text-slate-500">
                Diese Praxis hat {filialenCount} {filialenCount === 1 ? 'Filiale' : 'Filialen'} —
                sie kann deshalb nicht selbst zur Filiale werden.
              </p>
            ) : (
              <p className="mt-1 text-xs text-slate-500">
                Optional. Wenn diese Praxis eine Filiale ist, hier die Hauptpraxis auswählen.
                Es sind genau 2 Ebenen erlaubt.
              </p>
            )}
          </div>

          {isFuehrungskraftOrAdmin && (
            <div className="sm:col-span-2">
              <label className="label">Empfohlen durch (Partner)</label>
              <select
                className="input mt-1"
                value={form.partner}
                onChange={handleChange('partner')}
              >
                <option value="">— kein Empfehler —</option>
                {partnerListe.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Nur für Führungskräfte sichtbar.
              </p>
            </div>
          )}
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="mb-1 text-sm font-semibold text-slate-800">Umsatzpotenzial</div>
          <p className="mb-3 text-xs text-slate-500">
            Vom Vertriebler erfragt. Beide Felder optional, Eingabe in Euro pro Jahr.
          </p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label">GOÄ Jahresumsatz</label>
              <div className="relative mt-1">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  className="input pr-10"
                  placeholder="z. B. 250000"
                  value={form.umsatz_goae}
                  onChange={handleChange('umsatz_goae')}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                  €
                </span>
              </div>
            </div>
            <div>
              <label className="label">UV-GOÄ Jahresumsatz</label>
              <div className="relative mt-1">
                <input
                  type="number"
                  min="0"
                  step="1000"
                  className="input pr-10"
                  placeholder="z. B. 80000"
                  value={form.umsatz_uvgoae}
                  onChange={handleChange('umsatz_uvgoae')}
                />
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-slate-500">
                  €
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="mb-1 text-sm font-semibold text-slate-800">Praxissoftware</div>
          <p className="mb-3 text-xs text-slate-500">
            Welche Praxissoftware ist im Einsatz? Mehrfachauswahl möglich.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {SOFTWARE_OPTIONS.map((opt) => {
              const checked = (form.software ?? []).includes(opt.value);
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition ${
                    checked
                      ? 'border-primary-300 bg-primary-50 text-primary-900'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    checked={checked}
                    onChange={() => toggleSoftware(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
          {(form.software ?? []).includes('sonstiges') && (
            <div className="mt-3">
              <label className="label">Sonstige Software</label>
              <input
                type="text"
                className="input mt-1"
                placeholder="z. B. Eigenentwicklung …"
                value={form.software_sonstiges}
                onChange={handleChange('software_sonstiges')}
              />
            </div>
          )}
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="mb-1 text-sm font-semibold text-slate-800">Abrechnungsstelle</div>
          <p className="mb-3 text-xs text-slate-500">
            Wer rechnet die Privatliquidation ab?
          </p>
          <select
            className="input"
            value={form.abrechnungsstelle}
            onChange={handleChange('abrechnungsstelle')}
          >
            <option value="">— nicht erfasst —</option>
            {ABRECHNUNGSSTELLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {form.abrechnungsstelle === 'sonstiges' && (
            <div className="mt-3">
              <label className="label">Sonstige Abrechnungsstelle</label>
              <input
                type="text"
                className="input mt-1"
                placeholder="Name der Abrechnungsstelle"
                value={form.abrechnungsstelle_sonstiges}
                onChange={handleChange('abrechnungsstelle_sonstiges')}
              />
            </div>
          )}
        </div>

        <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
          <div className="mb-1 text-sm font-semibold text-slate-800">Beste Anrufzeiten</div>
          <p className="mb-3 text-xs text-slate-500">
            Wann ist die Praxis gut erreichbar? Felder leer lassen, wenn unbekannt —
            dann bleibt die Ampel auf der Praxis grau.
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {[
              { field: 'anruf_vm_von', label: 'Vormittag von' },
              { field: 'anruf_vm_bis', label: 'Vormittag bis' },
              { field: 'anruf_nm_von', label: 'Nachmittag von' },
              { field: 'anruf_nm_bis', label: 'Nachmittag bis' },
            ].map((row) => (
              <div key={row.field}>
                <label className="label">{row.label}</label>
                <select
                  className="input mt-1"
                  value={form[row.field] || ''}
                  onChange={handleChange(row.field)}
                >
                  {buildAnrufzeitOptions(form[row.field]).map((opt) => (
                    <option key={opt.value || '__leer'} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            ))}
          </div>

          <div className="mt-4 space-y-3">
            {[
              { field: 'anruf_kein_vm', label: 'Kein Vormittag an:' },
              { field: 'anruf_kein_nm', label: 'Kein Nachmittag an:' },
              { field: 'anruf_geschlossen', label: 'Geschlossen an:' },
            ].map((row) => {
              const selected = Array.isArray(form[row.field]) ? form[row.field] : [];
              return (
                <div key={row.field}>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {row.label}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {ANRUF_TAGE.map((tag) => {
                      const checked = selected.includes(tag.value);
                      return (
                        <label
                          key={tag.value}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition ${
                            checked
                              ? 'border-primary-300 bg-primary-50 text-primary-900'
                              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                            checked={checked}
                            onChange={() => toggleTag(row.field, tag.value)}
                          />
                          {tag.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-3 text-sm text-red-700 ring-1 ring-red-200">
            {error}
          </div>
        )}

        <div className="sticky bottom-0 -mx-6 -mb-5 mt-6 flex justify-end gap-2 border-t border-slate-200 bg-white px-6 py-4">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </button>
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Speichern …' : 'Speichern'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
